import React, { useCallback, memo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, CalendarIcon, UserIcon, TagIcon, ArrowTopRightOnSquareIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { useEpicDetails } from '../../../utils/epicApi'
import { CosmosEpicAnalysisDocument } from '@jirax/common'
import { useEpicDetailsNavigation } from '../../../utils/useNavigationState'
import { useNotificationHandler } from '../../../utils/useNotificationHandler'
import { Loading, Error } from '../../Elements/Indicators'
import { Card } from '../../Elements/Cards'
import { Header2, Header3, Header4, PMedium, PSmallFaded } from '../../Elements/Type'
import { JiraContentRenderer, extractTextFromADF } from '../../Elements/JiraContentRenderer'

/**
 * Extract plain text from malformed or partial JSON strings
 * This is a fallback when JSON parsing fails
 */
const extractTextFromPartialJSON = (jsonStr: string): string => {
  try {
    // Use regex to extract text content from JSON-like strings
    const textMatches = jsonStr.match(/"text":"([^"\\]*(\\.[^"\\]*)*)"/g)
    if (textMatches) {
      const texts = textMatches
        .map(match => {
          // Extract the text value from "text":"value"
          const textMatch = match.match(/"text":"([^"\\]*(\\.[^"\\]*)*)"/)
          return textMatch ? textMatch[1] : ''
        })
        .filter(text => text.trim().length > 0)
        .map(text => {
          // Unescape common JSON escape sequences
          return text
            .replace(/\\"/g, '"')
            .replace(/\\\\/g, '\\')
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
        })

      return texts.join(' ').trim()
    }

    // If no text matches found, return empty string
    return ''
  } catch (error) {
    console.warn('Failed to extract text from partial JSON:', error)
    return ''
  }
}
import { NavigationState } from '../../../types/epic-types'

const EpicDetailsPage: React.FC = memo(() => {
  const { epicKey } = useParams<{ epicKey: string }>()
  const navigate = useNavigate()

  // Initialize notification handler
  useNotificationHandler()

  // Navigation state management for epic details
  const getCurrentNavigationState = useCallback((): NavigationState => ({
    scrollPosition: window.pageYOffset || document.documentElement.scrollTop || 0,
    currentPage: 1, // Details page doesn't have pagination
    filters: {
      projectKey: '', // Will be populated from epic data if needed
      dateRange: { startDate: '', endDate: '' }
    }
  }), [])

  const { saveState } = useEpicDetailsNavigation(getCurrentNavigationState)

  // Enhanced navigation handler that preserves state
  const handleBackToEpics = useCallback(() => {
    // Save current details page state (mainly scroll position)
    saveState()
    // Navigate back to epics list
    navigate('/epics')
  }, [navigate, saveState])

  // Fetch epic details
  const {
    isLoading,
    error,
    data: epicData,
    refetch: refetchEpicDetails,
    isFetching,
    status,
    fetchStatus
  } = useEpicDetails({
    epicKey: epicKey || '',
    enabled: !!epicKey
  })

  // Show loading only when we have no data and no error
  if (!epicData && !error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <Loading message={`Loading epic ${epicKey}...`} />
        </div>
      </div>
    )
  }



  if (!epicKey) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <Error message="Epic key is required" />
        </div>
      </div>
    )
  }



  if (error) {
    // Check if this is a 404 error indicating the epic hasn't been analyzed yet
    const errorMessage = (error as Error).message
    const isAnalysisNotFound = errorMessage.includes('404') || errorMessage.includes('not found') || errorMessage.includes('not have been analyzed')

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <nav className="mb-6" aria-label="Breadcrumb">
            <button
              onClick={handleBackToEpics}
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer"
              type="button"
              aria-label="Go back to epic list"
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Epics
            </button>
          </nav>

          {isAnalysisNotFound ? (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                  <CalendarIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                </div>
                <Header2 title="Analysis Not Complete" className="text-gray-900 mb-2" />
                <div className="text-gray-500 mb-6">
                  <p className="text-lg">
                    Epic <span className="font-semibold text-gray-900">{epicKey}</span> has not been analyzed yet.
                    You can start the analysis from the epics list page.
                  </p>
                </div>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={handleBackToEpics}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <ArrowLeftIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                    Back to Epics
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Error
              title="Failed to Load Epic"
              message={`Unable to load epic ${epicKey}: ${errorMessage}`}
              onRetry={() => refetchEpicDetails()}
              retryText="Retry Loading"
              actionable={true}
            />
          )}
        </div>
      </div>
    )
  }

  if (!epicData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <nav className="mb-6" aria-label="Breadcrumb">
            <button
              onClick={handleBackToEpics}
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer"
              type="button"
              aria-label="Go back to epic list"
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Epics
            </button>
          </nav>
          <Error message={`Epic ${epicKey} not found`} />
        </div>
      </div>
    )
  }

  // Handle the case where epicData might be the API response wrapper or the actual data
  let actualData = epicData
  if (epicData && 'success' in epicData && 'data' in epicData) {
    // If epicData is the API response wrapper, extract the data
    actualData = epicData.data as CosmosEpicAnalysisDocument
  }

  // Safely destructure with fallbacks
  const { epic, metrics, tickets, timeline } = actualData || {}

  // Additional safety check - if any required data is missing, show loading
  if (!epic || !metrics || !tickets || !timeline) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <Loading message={`Loading epic ${epicKey}...`} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation back to epics list */}
        <nav className="mb-6" aria-label="Breadcrumb">
          <button
            onClick={handleBackToEpics}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-150 bg-transparent border-none cursor-pointer"
            type="button"
            aria-label="Go back to epic list"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            Back to Epics
          </button>
        </nav>

        {/* Epic Header */}
        <section aria-labelledby="epic-header">
          <EpicHeader epic={epic} />
        </section>

        {/* Epic Metrics */}
        <section aria-labelledby="epic-metrics">
          <EpicMetrics metrics={metrics} />
        </section>

        {/* Epic Timeline and Tickets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <section aria-labelledby="epic-timeline">
            <EpicTimeline timeline={timeline} />
          </section>
          <section aria-labelledby="epic-tickets">
            <EpicTickets tickets={tickets} metrics={metrics} />
          </section>
        </div>
      </div>
    </div>
  )
})

// Epic Header Component
interface EpicHeaderProps {
  epic: any
}

const EpicHeader: React.FC<EpicHeaderProps> = memo(({ epic }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Card className="p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <h2 id="epic-header" className="text-2xl font-bold text-indigo-600">{epic.key}</h2>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {epic.project.key}
              </span>
            </div>
            <a
              href={`https://cnsgitbits.atlassian.net/browse/${epic.key}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors duration-150"
              aria-label={`View ${epic.key} in Jira`}
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
              View in Jira
            </a>
          </div>

          <Header3 title={epic.title} className="mb-4" />

          <div className="mb-4 text-gray-700">
            {epic.description ? (
              (() => {
                // Check if description is a JSON object (ADF format)
                if (typeof epic.description === 'object' && epic.description !== null) {
                  try {
                    return (
                      <JiraContentRenderer
                        content={epic.description}
                        className="text-sm text-gray-700"
                      />
                    )
                  } catch (error) {
                    console.warn(`Failed to render Jira content for epic ${epic.key}:`, error)
                    // Fallback to plain text extraction
                    const plainText = extractTextFromADF(epic.description)
                    return plainText ? (
                      <div className="text-sm text-gray-700">{plainText}</div>
                    ) : (
                      <div className="text-sm text-gray-600 italic">
                        Rich text description available (content from Jira)
                      </div>
                    )
                  }
                } else {
                  // Handle plain text descriptions
                  const descStr = String(epic.description)
                  console.log(`Epic ${epic.key} description length:`, descStr.length)
                  console.log(`Epic ${epic.key} description (first 200 chars):`, descStr.substring(0, 200))
                  console.log(`Epic ${epic.key} description (last 200 chars):`, descStr.substring(Math.max(0, descStr.length - 200)))

                  if (descStr.startsWith('{') || descStr.startsWith('[')) {
                    try {
                      // Try to clean up the JSON string before parsing
                      const cleanedStr = descStr.trim()

                      // Check if the string appears to be truncated (doesn't end with } or ])
                      if (!cleanedStr.endsWith('}') && !cleanedStr.endsWith(']')) {
                        console.warn(`Epic ${epic.key} description appears truncated, attempting to extract plain text`)
                        // Try to extract plain text from the partial JSON
                        const plainText = extractTextFromPartialJSON(cleanedStr)
                        if (plainText) {
                          return <div className="text-sm text-gray-700">{plainText}</div>
                        }
                      }

                      const parsedContent = JSON.parse(cleanedStr)
                      console.log(`Epic ${epic.key} parsed description successfully`)
                      return (
                        <JiraContentRenderer
                          content={parsedContent}
                          className="text-sm text-gray-700"
                        />
                      )
                    } catch (error) {
                      console.warn(`Failed to parse JSON for epic ${epic.key}:`, error)
                      // Try to extract plain text from the malformed JSON
                      const plainText = extractTextFromPartialJSON(descStr)
                      if (plainText) {
                        return <div className="text-sm text-gray-700">{plainText}</div>
                      }
                      // Final fallback
                      return (
                        <div className="text-sm text-gray-600 italic">
                          Rich text description available (formatted content from Jira)
                        </div>
                      )
                    }
                  } else {
                    return <PMedium text={descStr} />
                  }
                }
              })()
            ) : (
              <div className="text-sm text-gray-500 italic">
                No epic description entered.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <UserIcon className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Creator:</span>
              <span className="font-medium">
                {epic.creator?.displayName || epic.creator?.name || 'Unknown'}
              </span>
            </div>

            {epic.assignee && (
              <div className="flex items-center space-x-2">
                <UserIcon className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Assignee:</span>
                <span className="font-medium">
                  {epic.assignee?.displayName || epic.assignee?.name || 'Unknown'}
                </span>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Created:</span>
              <span className="font-medium">{formatDate(epic.created)}</span>
            </div>

            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Resolved:</span>
              <span className="font-medium">{formatDate(epic.resolved)}</span>
            </div>

            {epic.labels.length > 0 && (
              <div className="flex items-center space-x-2 md:col-span-2">
                <TagIcon className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Labels:</span>
                <div className="flex flex-wrap gap-1">
                  {epic.labels.map((label: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
})

EpicHeader.displayName = 'EpicHeader'

// Epic Metrics Component
interface EpicMetricsProps {
  metrics: any
}

const EpicMetrics: React.FC<EpicMetricsProps> = memo(({ metrics }) => {
  // Calculate scope expansion percentage
  const scopeExpansionPercentage = metrics.initialPointsEstimate > 0
    ? Math.round((metrics.pointsAdded / metrics.initialPointsEstimate) * 100)
    : 0

  return (
    <Card className="p-6 mb-6">
      <h3 id="epic-metrics" className="text-lg font-medium text-gray-900 mb-4">Analysis Results</h3>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
          <div className="text-2xl font-bold text-indigo-900 mb-1">
            {scopeExpansionPercentage}%
          </div>
          <div className="text-sm text-indigo-700">Scope Expansion</div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {metrics.daysFromStartToCompletion}
            {metrics.daysFromOriginalDueToActual > 0 && (
              <span className="text-base font-medium text-red-600 ml-2">
                (+{metrics.daysFromOriginalDueToActual})
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600">Days to Complete</div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {metrics.finalPointsTotal}
            {metrics.pointsAdded > 0 && (
              <span className="text-base font-medium text-orange-600 ml-2">
                (+{metrics.pointsAdded})
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600">Total Points</div>
        </div>

        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <div className="text-2xl font-bold text-orange-900 mb-1">{metrics.ticketsAddedAfterStart}</div>
          <div className="text-sm text-orange-700">Tickets Added</div>
        </div>

        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-900 mb-1">{metrics.bugsAdded}</div>
          <div className="text-sm text-red-700">Bugs Added</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Header4 title="Scope Changes" />
          <div className="mt-2 space-y-2">
            <div className="flex justify-between">
              <PSmallFaded text="Initial Points Estimate:" />
              <span className="font-medium">{metrics.initialPointsEstimate}</span>
            </div>
            <div className="flex justify-between">
              <PSmallFaded text="Final Points Total:" />
              <span className="font-medium">{metrics.finalPointsTotal}</span>
            </div>
            <div className="flex justify-between">
              <PSmallFaded text="Points Added:" />
              <span className={`font-medium ${metrics.pointsAdded > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                +{metrics.pointsAdded}
              </span>
            </div>
          </div>
        </div>

        <div>
          <Header4 title="Timeline Changes" />
          <div className="mt-2 space-y-2">
            <div className="flex justify-between">
              <PSmallFaded text="Due Date Changes:" />
              <span className={`font-medium ${metrics.dueDateChangeCount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {metrics.dueDateChangeCount}
              </span>
            </div>
            {metrics.daysFromOriginalDueToActual !== undefined && (
              <div className="flex justify-between">
                <PSmallFaded text="Days from Original Due:" />
                <span className={`font-medium ${metrics.daysFromOriginalDueToActual > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {metrics.daysFromOriginalDueToActual > 0 ? '+' : ''}{metrics.daysFromOriginalDueToActual}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
})

EpicMetrics.displayName = 'EpicMetrics'

// Epic Timeline Component
interface EpicTimelineProps {
  timeline: any
}

const EpicTimeline: React.FC<EpicTimelineProps> = memo(({ timeline }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Card className="p-6">
      <h3 id="epic-timeline" className="text-lg font-medium text-gray-900 mb-4">Epic Timeline</h3>

      {/* Timeline Summary */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <PSmallFaded text="Start Date" />
          <div className="font-medium">{formatDate(timeline.startDate)}</div>
        </div>
        <div>
          <PSmallFaded text="Completion Date" />
          <div className="font-medium">{formatDate(timeline.actualCompletionDate)}</div>
        </div>
        {timeline.originalDueDate && (
          <div>
            <PSmallFaded text="Original Due Date" />
            <div className="font-medium">{formatDate(timeline.originalDueDate)}</div>
          </div>
        )}
      </div>

      {/* Status Changes */}
      {timeline.statusChanges && timeline.statusChanges.length > 0 && (
        <div className="mb-6">
          <Header4 title="Status Changes" />
          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
            {timeline.statusChanges
              .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
              .map((change: any, index: number) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <div className="text-sm">
                      <span className="text-gray-500">{change.fromStatus}</span>
                      <span className="mx-2 text-gray-400">→</span>
                      <span className="font-medium text-gray-900">{change.toStatus}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    <div>{formatDateTime(change.date)}</div>
                    <div>{change.author.displayName}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Scope Changes */}
      {timeline.scopeChanges && timeline.scopeChanges.length > 0 && (
        <div className="mb-6">
          <Header4 title="Scope Changes" />
          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
            {timeline.scopeChanges
              .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
              .map((change: any, index: number) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${change.action === 'added' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                      {change.action}
                    </span>
                    <div className="text-sm">
                      <span className="font-medium">{change.ticketKey}</span>
                      <span className="text-gray-500 ml-2">({change.ticketType})</span>
                      {change.storyPoints && (
                        <span className="text-gray-500 ml-2">{change.storyPoints} pts</span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    <div>{formatDateTime(change.date)}</div>
                    <div>{change.author.displayName}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Due Date Changes */}
      {timeline.dueDateChanges && timeline.dueDateChanges.length > 0 && (
        <div>
          <Header4 title="Due Date Changes" />
          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
            {timeline.dueDateChanges
              .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
              .map((change: any, index: number) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="text-sm">
                    <span className="text-gray-500">{change.fromDate ? formatDate(change.fromDate) : 'No date'}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className="font-medium text-gray-900">{change.toDate ? formatDate(change.toDate) : 'No date'}</span>
                    {change.reason && (
                      <div className="text-xs text-gray-500 mt-1">{change.reason}</div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    <div>{formatDateTime(change.date)}</div>
                    <div>{change.author.displayName}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </Card>
  )
})

EpicTimeline.displayName = 'EpicTimeline'

// Epic Tickets Component
interface EpicTicketsProps {
  tickets: any
  metrics: any
}

const EpicTickets: React.FC<EpicTicketsProps> = memo(({ tickets, metrics }) => {
  const [showOnlyAddedLater, setShowOnlyAddedLater] = useState(false)

  const getStatusColor = (status: string, statusCategoryKey?: string) => {
    // Use statusCategoryKey for locale-independent color mapping
    if (statusCategoryKey === 'done') return 'bg-green-100 text-green-800'
    if (statusCategoryKey === 'indeterminate') return 'bg-blue-100 text-blue-800'
    if (statusCategoryKey === 'new') return 'bg-gray-100 text-gray-800'
    // Fallback to name-based matching for older data without statusCategoryKey
    const lowerStatus = status.toLowerCase()
    if (lowerStatus.includes('done') || lowerStatus.includes('resolved') || lowerStatus.includes('closed')) {
      return 'bg-green-100 text-green-800'
    }
    if (lowerStatus.includes('progress') || lowerStatus.includes('review')) {
      return 'bg-blue-100 text-blue-800'
    }
    if (lowerStatus.includes('todo') || lowerStatus.includes('open') || lowerStatus.includes('new')) {
      return 'bg-gray-100 text-gray-800'
    }
    return 'bg-yellow-100 text-yellow-800'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  const handleTicketClick = (ticketKey: string) => {
    window.open(`https://cnsgitbits.atlassian.net/browse/${ticketKey}`, '_blank', 'noopener,noreferrer')
  }

  // Filter tickets based on the toggle
  const filteredTickets = showOnlyAddedLater
    ? tickets.tickets.filter((ticket: any) => ticket.addedAfterEpicStart)
    : tickets.tickets

  return (
    <Card className="p-6">
      <h3 id="epic-tickets" className="text-lg font-medium text-gray-900 mb-4">Epic Tickets</h3>

      {/* Tickets Summary */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900 mb-1">{tickets.totalTickets}</div>
          <div className="text-sm text-gray-600">Total Tickets</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900 mb-1">{tickets.totalPoints}</div>
          <div className="text-sm text-gray-600">Total Points</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <div className="text-2xl font-bold text-orange-900 mb-1">
            {tickets.tickets.filter((t: any) => t.addedAfterEpicStart).length}
          </div>
          <div className="text-sm text-orange-700">Tickets Added</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <div className="text-2xl font-bold text-orange-900 mb-1">
            +{metrics.pointsAdded}
          </div>
          <div className="text-sm text-orange-700">Points Added</div>
        </div>
      </div>

      {/* Tickets by Type */}
      {Object.keys(tickets.ticketsByType).length > 0 && (
        <div className="mb-6">
          <Header4 title="Tickets by Type" />
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(tickets.ticketsByType).map(([type, count]) => (
              <div key={type} className="flex justify-between items-center py-1">
                <PSmallFaded text={type} />
                <span className="font-medium">{count as number}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tickets List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Header4 title={showOnlyAddedLater ? "Tickets Added Later" : "All Tickets"} />
          <button
            onClick={() => setShowOnlyAddedLater(!showOnlyAddedLater)}
            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${showOnlyAddedLater
              ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            title={showOnlyAddedLater ? "Show all tickets" : "Show only tickets added after epic start"}
          >
            <FunnelIcon className="h-4 w-4 mr-1" />
            {showOnlyAddedLater ? "Show All" : "Added Later"}
          </button>
        </div>

        <div className="mt-2 space-y-2 max-h-96 overflow-y-auto">
          {filteredTickets
            .sort((a: any, b: any) => {
              // Sort by resolution date (earliest to latest)
              // Put unresolved tickets at the end
              if (!a.resolved && !b.resolved) return 0
              if (!a.resolved) return 1
              if (!b.resolved) return -1
              return new Date(a.resolved).getTime() - new Date(b.resolved).getTime()
            })
            .map((ticket: any) => (
              <div
                key={ticket.key}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 hover:border-indigo-300"
                onClick={() => handleTicketClick(ticket.key)}
                title={`Click to view ${ticket.key} in Jira`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-sm text-indigo-600 hover:text-indigo-800">{ticket.key}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status, ticket.statusCategoryKey)}`}>
                        {ticket.status}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {ticket.issueType}
                      </span>
                      {ticket.addedAfterEpicStart && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Added Later
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-900 mb-2">{ticket.summary}</div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Created: {formatDate(ticket.created)}</span>
                      {ticket.resolved && <span>Resolved: {formatDate(ticket.resolved)}</span>}
                      {ticket.storyPoints && <span>{ticket.storyPoints} pts</span>}
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    {ticket.assignee ? (
                      <div className="text-xs text-gray-500 text-right">
                        <div>Assignee:</div>
                        <div className="font-medium">{ticket.assignee.displayName || ticket.assignee.name || 'Unknown'}</div>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 text-right">
                        <div>Assignee:</div>
                        <div className="font-medium text-gray-400">Unassigned</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>

        {showOnlyAddedLater && filteredTickets.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FunnelIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>No tickets were added after the epic started.</p>
          </div>
        )}

        {showOnlyAddedLater && filteredTickets.length > 0 && (
          <div className="mt-3 text-xs text-gray-500 text-center">
            Showing {filteredTickets.length} of {tickets.tickets.length} tickets
          </div>
        )}
      </div>
    </Card>
  )
})

EpicTickets.displayName = 'EpicTickets'

EpicDetailsPage.displayName = 'EpicDetailsPage'

export default EpicDetailsPage